<template>
  <!-- Reuses .product-options styling so it visually matches Color / Size / Inseam selectors -->
  <div v-if="styles.length" class="product-options program-styles">
    <div class="product-options__option">
      <div class="product-options__title">
        <span>{{ heading.toUpperCase() }} -</span>
        <div class="product-options__title--selected">
          <span>{{ selectedLabel }}</span>
        </div>
      </div>

      <div class="product-options__other-options">
        <template v-for="(style, index) in styles" :key="index">
          <!-- Current style: static, not a link -->
          <span
            v-if="style.current"
            class="product-options__item selected"
            aria-current="true"
          >
            <span class="product-options__item-name">{{ style.label }}</span>
          </span>

          <!-- Other styles: navigate to their PDP (whole pill is clickable) -->
          <a
            v-else
            :href="style.url"
            class="product-options__item"
            data-stio-gtm-id="PDP: Program Style"
            :aria-label="`View ${style.label}`"
            @pointerenter="onStylePrefetch(style)"
            @click="onStyleClick($event, style)"
          >
            <span class="product-options__item-name">{{ style.label }}</span>
          </a>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, toRefs, onMounted } from 'vue';

  const props = defineProps({
    program: {
      type: Object,
      default: () => ({}),
    },
  });

  const { program } = toRefs(props);

  // Fixed heading word for the selector, e.g. "STYLE - Hooded Jacket"
  const heading = 'Style';

  const styles = computed(() => program.value?.styles || []);

  // Show the current product's own style label as the selected value.
  // Prefer current_label (always the product being viewed); fall back to the
  // style flagged as current in the list.
  const selectedLabel = computed(() => {
    if (program.value?.current_label) return program.value.current_label;
    const current = styles.value.find((style) => style.current);
    return current ? current.label : '';
  });

  // Switch styles in-place (no full page redirect). The href stays intact so
  // middle-click / cmd-click / no-JS all still open the PDP normally.
  const onStyleClick = (event, style) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    window.dispatchEvent(
      new CustomEvent('pdp:switch-style', {
        detail: { url: style.url, handle: style.handle, label: style.label },
      })
    );
  };

  const prefetch = (url) => {
    if (url) {
      window.dispatchEvent(new CustomEvent('pdp:prefetch-style', { detail: { url } }));
    }
  };

  // Prefetch the target product's data on hover/touch so the click feels instant.
  const onStylePrefetch = (style) => prefetch(style.url);

  // Cap how many siblings we pre-warm on load so a large program doesn't fire a
  // burst of full-page fetches. Hover still prefetches anything beyond the cap.
  const PREFETCH_LIMIT = 6;

  // Warm the cache for sibling styles once the browser is idle, so the very
  // first click is instant too (not just clicks preceded by a hover).
  onMounted(() => {
    const warmAll = () => {
      styles.value
        .filter((style) => !style.current)
        .slice(0, PREFETCH_LIMIT)
        .forEach((style) => prefetch(style.url));
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warmAll, { timeout: 2500 });
    } else {
      setTimeout(warmAll, 1200);
    }
  });
</script>
